<template>
  <div
    v-if="!isCartEmpty"
    :class="$blockClass(blockClass, 'shipping-container')"
    class="shipping-notification"
  >
    <p
      :class="$blockClass(blockClass, 'shipping-label')"
      class="caption shipping-notification__label"
      v-text="$cleanRTE(label)"
    />
    <div
      :class="$blockClass(blockClass, 'shipping-progress-outer')"
      class="shipping-notification__progress-outer"
    >
      <div
        :class="$blockClass(blockClass, 'shipping-progress-inner')"
        :style="`width: ${progress};`"
        class="shipping-notification__progress-inner"
      />
    </div>
  </div>
</template>

<script>

/**
 * Vue: Shipping notification (shipping-notification)
 * -----------------------------------------------------------------------------
 * A notification callout that will display a customer's progress towards free shipping.
 *
 * @param {Number} thresholdValue - The value of the cart that needs to be
 * met in order to qualify for free shipping.
 * @param {String} thresholdBlank - The empty cart message.
 * @param {String} thresholdMet - The message when the cart price meets the
 * threshold.
 * @param {String} thresholdUnmet - The message when the cart price does not
 * meet the threshold.
 */

import { mapState } from 'vuex'

export default {
  name: 'ShippingNotification',

  props: {
    blockClass: {
      type: String,
      default: '',
    },
    thresholdValue: {
      type: [Number, String],
      default: 0,
    },
    thresholdBlank: {
      type: String,
      default: '',
    },
    thresholdMet: {
      type: String,
      default: '',
    },
    thresholdUnmet: {
      type: String,
      default: '',
    },
  },

  data() {
    return {
      global: true,
    }
  },

  computed: {

    /**
     * Map Vuex state.
     */
    ...mapState({
      cart: (state) => state.cart.response,
      cartStatus: (state) => state.cart.status,
    }),

    /**
     * Shipping notification label.
     * @returns {string|*}
     */
    label() {
      if (this.cart.total_price === 0) {
        return this.blankFormatted
      }

      return this.cart.total_price >= this.thresholdValue ?
             this.thresholdMet :
             this.unmetFormatted
    },

    /**
     * Replace placeholders in the empty cart message.
     * @returns {string}
     */
    blankFormatted() {
      const formattedValue = this.$formatMoney(this.thresholdValue)
      return this.thresholdBlank.replaceAll('#price#', formattedValue)
    },

    /**
     * Replace placeholders in the threshold unmet message.
     * @returns {string}
     */
    unmetFormatted() {
      return this.thresholdUnmet.replaceAll('#price#', this.priceDifference)
    },

    /**
     * Get formatted price difference between threshold amount and cart price
     * @returns {string}
     */
    priceDifference() {
      return this.$formatMoney(this.thresholdValue - this.cart.total_price)
    },

    /**
     * Compute the user's progress in meeting the threshold.
     * @returns {string} - The percentage string
     */
    progress() {
      let percent = (this.cart.total_price / this.thresholdValue) * 100

      return `${percent}%`
    },

    /**
     * Compute if cart is empty.
     * @returns {boolean}
     */
    isCartEmpty() {
      return this.cart.item_count < 1 && this.cartStatus.label !== 'adding'
    },
  },

  mounted() {
    // Not empty
  },

  methods: {
    // Not empty
  },
}
</script>
